import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Button, ButtonLink } from '@blueprint/button';
import { Text } from '@blueprint/text';
import { IconSize, PropsWithZooplaTheme, ZooplaColor } from '@blueprint/theme';
import { RequireKeys } from '@blueprint/utils';
import { Icon, Icons } from '@blueprint/ui';

export type IconProps = {
    /** The ID of the icon to display */
    iconId: typeof Icons;
    /** The color of the icon */
    color?: ZooplaColor;
    /** The stroke color of the icon */
    strokeColor?: ZooplaColor;
    /**
     * The size of the icon
     * @default 'small'
     */
    size?: IconSize;
    /** A custom class name */
    className?: string;
};

export type DefaultedProps = RequireKeys<IconProps, 'color'> &
    RequireKeys<IconProps, 'size'>;

const StyledIcon = styled('span')<DefaultedProps>(
    (props) =>
        css`
            display: inline-block;
            width: ${props.theme.iconSize[props.size]};
            height: ${props.theme.iconSize[props.size]};
            vertical-align: text-top;
            fill: ${props.theme.colors[props.color]};
            ${props.strokeColor
                ? `stroke: ${props.theme.colors[props.strokeColor]};`
                : null}
        `
);

export type Props = {
    iconPosition?: 'left' | 'right';
    iconOnly?: boolean;
};

export type ButtonIconOnlyProps = Props & {
    /**
     * Buttons with no text and only icons should provide an aria-label
     */
    'aria-label': string;
};

const styles = ({
    theme,
    iconPosition = 'left',
    iconOnly
}: PropsWithZooplaTheme<Props>) => css`
    /** Add margin to both:
     - Icon component (which has a fixed width <span> wrapper)
     - SVG tag for @blueprint/icon components
     **/
    ${Icon}, svg {
        fill: currentColor;
        ${`margin-${iconPosition === 'left' ? 'right' : 'left'}: ${
            theme.spacing['spacing-1']
        };`}
        ${iconPosition === 'right' && 'order:2;'}
    }

    ${Icon} > svg {
        /** Reset margin for the <svg> in Icon component **/
        ${`margin-${iconPosition === 'left' ? 'right' : 'left'}: 0;`}
    }

    ${Text} {
        color: currentColor;
        font-weight: ${theme.type.semiBold};
        display: ${iconOnly ? `none` : `block`};
    }

    ${iconOnly &&
    `
        ${StyledIcon},  svg {
            margin-right: 0;
            display: flex;
            flex: 1 0 auto;
        }
    `}
`;

/**
 - ButtonWithIcon is a utility version of `Button` that adds support for icons as children
 - ButtonIconOnly is a utility version of `Button` that adds support for icons only as children
 - Only icons (`Icon` or `@blueprint/icon` components), `Text` or plain text should be used as children
 - `ButtonLinkWithIcon` is also exported if you need an "a" base HTML element
 - You should not use nested elements in an HTML input (it is not accessible) so we don't export a `ButtonInputWithIcon` variant. If you really need to you could achieve it with `ButtonWithIcon.withComponent('input')`.
**/
/**
 * @deprecated use ZDS
 */
const ButtonWithIcon = styled(Button)<Props>(styles);

/**
 * @deprecated use ZDS
 */
const ButtonIconOnly = styled(Button)<ButtonIconOnlyProps>(styles);

/**
 * @deprecated use ZDS
 */
const ButtonLinkWithIcon = styled(ButtonLink)<Props>(styles);

export { ButtonWithIcon, ButtonLinkWithIcon, ButtonIconOnly };
